/**
 * `/licenses` (OCP-299) - operator surface for self-host license CRUD.
 *
 * Full management UI over the OCP-116 license authority (Ed25519 JWT
 * signing, mint/revoke/rotate-keys API): list, filter-by-customer, mint
 * (with a one-time key reveal), per-row revoke, and signing-key
 * rotation. Detail + linked-instances live in AdminLicenseDetailPage.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';
import { fmtDate } from '../lib/format-time';

type Tier = 'solo' | 'team' | 'business' | 'enterprise';
type LicenseStatus = 'active' | 'revoked' | 'expired';

interface LicenseRow {
  id: string;
  display_name: string;
  tier: Tier;
  status: LicenseStatus;
  max_instances: number | null;
  features: string[];
  air_gapped: boolean;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  license_key_prefix: string;
  subscription_id: string | null;
}

interface AccountHit {
  id: string;
  email: string;
  display_name: string | null;
  company_name: string | null;
}

const TIERS: Tier[] = ['solo', 'team', 'business', 'enterprise'];

export function AdminLicensesPage() {
  const [items, setItems] = useState<LicenseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [customerFilter, setCustomerFilter] = useState<AccountHit | null>(null);
  const [minting, setMinting] = useState(false);
  const [revoking, setRevoking] = useState<LicenseRow | null>(null);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    const qs = customerFilter ? `?customer_id=${customerFilter.id}` : '';
    try {
      const res = await api<{ items: LicenseRow[] }>(`/v1/admin/licenses${qs}`);
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Load failed (HTTP ${err.status}).` : 'Load failed.');
    } finally {
      setLoading(false);
    }
  }, [customerFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-lg font-semibold">Self-host licenses</h1>
          <p className="mt-1 text-xs text-gray-500">
            Issue, list and revoke self-host licenses for customers. They
            download their license file from the portal; their instance
            verifies it automatically.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setRotating(true)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Rotate signing keys
          </button>
          <button
            type="button"
            onClick={() => setMinting(true)}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Mint license
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <CustomerFilter value={customerFilter} onChange={setCustomerFilter} />
        <div className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${items.length} shown`}
        </div>
      </div>

      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <div className="rounded border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
          No licenses{customerFilter ? ' for this customer' : ''}.
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">License</th>
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Key</th>
                <th className="px-3 py-2">Instances</th>
                <th className="px-3 py-2">Validity</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {items.map((l) => (
                <tr key={l.id} className={l.status !== 'active' ? 'opacity-60' : ''}>
                  <td className="px-3 py-2">
                    <Link to={`/licenses/${l.id}`} className="font-medium text-brand-700 hover:underline">
                      {l.display_name}
                    </Link>
                    {l.air_gapped && (
                      <span className="ml-2 rounded border border-gray-200 dark:border-gray-700 px-1.5 py-0.5 text-[10px] uppercase text-gray-500">
                        air-gapped
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs capitalize">{l.tier}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{l.license_key_prefix}…</td>
                  <td className="px-3 py-2 text-xs tabular-nums">
                    {l.max_instances === null ? '∞' : l.max_instances}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    <div>{fmtDate(l.issued_at)}</div>
                    <div className="text-gray-400">
                      {l.expires_at ? `→ ${fmtDate(l.expires_at)}` : 'perpetual'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={l.status} />
                    {l.status === 'revoked' && l.revoked_reason && (
                      <div className="mt-0.5 text-xs text-gray-500">{l.revoked_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {l.status === 'active' && (
                      <button
                        type="button"
                        onClick={() => setRevoking(l)}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {minting && (
        <MintModal
          onCancel={() => setMinting(false)}
          onDone={async () => {
            setMinting(false);
            await load();
          }}
        />
      )}
      {revoking && (
        <RevokeModal
          row={revoking}
          onCancel={() => setRevoking(null)}
          onDone={async () => {
            setRevoking(null);
            await load();
          }}
        />
      )}
      {rotating && <RotateKeysModal onClose={() => setRotating(false)} />}
    </div>
  );
}

function StatusPill({ status }: { status: LicenseStatus }) {
  const cfg = {
    active: { label: 'active', cls: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300' },
    revoked: { label: 'revoked', cls: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    expired: { label: 'expired', cls: 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400' },
  }[status];
  return <span className={`rounded border px-2 py-0.5 text-xs ${cfg.cls}`}>{cfg.label}</span>;
}

/**
 * Customer search-picker. Filters the list and feeds the mint modal a
 * customer_id. Debounced search against /v1/admin/accounts.
 */
function CustomerFilter({
  value,
  onChange,
}: {
  value: AccountHit | null;
  onChange: (a: AccountHit | null) => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<AccountHit[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value || q.trim().length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api<{ items: AccountHit[] }>(
          `/v1/admin/accounts?search=${encodeURIComponent(q.trim())}&limit=10`,
        );
        if (!cancelled) {
          setHits(res.items);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setHits([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, value]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm">
        <span className="text-gray-500">Customer:</span>
        <span className="font-medium">{value.email}</span>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQ('');
          }}
          className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          aria-label="Clear customer filter"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Filter by customer (email / name)…"
        className="w-72 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1.5 text-sm"
      />
      {open && hits.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-64 w-72 overflow-auto rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          {hits.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(h);
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <div className="font-medium">{h.email}</div>
                {(h.display_name || h.company_name) && (
                  <div className="text-xs text-gray-500">
                    {[h.display_name, h.company_name].filter(Boolean).join(' · ')}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MintModal({
  onCancel,
  onDone,
}: {
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [customer, setCustomer] = useState<AccountHit | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [tier, setTier] = useState<Tier>('business');
  const [features, setFeatures] = useState('');
  const [maxInstances, setMaxInstances] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [airGapped, setAirGapped] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ license_id: string; license_key: string; jwt: string } | null>(null);

  async function submit() {
    if (submitting) return;
    if (!customer) {
      setError('Pick a customer first.');
      return;
    }
    if (!displayName.trim()) {
      setError('Display name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        customer_id: customer.id,
        display_name: displayName.trim(),
        tier,
        features: features
          .split(',')
          .map((f) => f.trim())
          .filter(Boolean),
        air_gapped: airGapped,
      };
      if (maxInstances.trim()) body.max_instances = Number(maxInstances.trim());
      if (expiresAt.trim()) body.expires_at = new Date(expiresAt.trim()).toISOString();
      const res = await api<{ license_id: string; license_key: string; jwt: string }>(
        '/v1/admin/licenses',
        { method: 'POST', body },
      );
      setMinted(res);
    } catch (err) {
      setError(err instanceof ApiError ? `Mint failed (HTTP ${err.status}).` : 'Mint failed.');
    } finally {
      setSubmitting(false);
    }
  }

  // After a successful mint the modal flips to a one-time reveal of the
  // license key + signed file. Closing it triggers the list reload.
  if (minted) {
    return (
      <ModalShell onClose={() => void onDone()} title="License minted">
        <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          Copy these now — the license key and signed file are shown only once and
          cannot be retrieved later. The customer also sees their license in the
          portal, but only you see the raw key here.
        </div>
        <CopyField label="License key" value={minted.license_key} mono />
        <CopyField label="Signed .orbotolic (JWT)" value={minted.jwt} mono multiline />
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => void onDone()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell onClose={onCancel} title="Mint self-host license">
      <div className="space-y-3">
        <Labeled label="Customer">
          {customer ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">{customer.email}</span>
              <button
                type="button"
                onClick={() => setCustomer(null)}
                className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                change
              </button>
            </div>
          ) : (
            <CustomerFilter value={null} onChange={setCustomer} />
          )}
        </Labeled>
        <Labeled label="Display name (customer-visible: shown in every licence mail and in the portal)">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Self-Host Business - internal team"
            className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
          />
        </Labeled>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Labeled label="Tier">
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value as Tier)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm capitalize"
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Labeled>
          <Labeled label="Max instances (blank = unlimited)">
            <input
              type="number"
              min={1}
              value={maxInstances}
              onChange={(e) => setMaxInstances(e.target.value)}
              placeholder="∞"
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </Labeled>
        </div>
        <Labeled label="Features (comma-separated)">
          <input
            value={features}
            onChange={(e) => setFeatures(e.target.value)}
            placeholder="e.g. sso, audit_export, priority_support"
            className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
          />
        </Labeled>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2">
          <Labeled label="Expires at (blank = perpetual)">
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
            />
          </Labeled>
          <label className="flex items-center gap-2 pb-1.5 text-sm">
            <input
              type="checkbox"
              checked={airGapped}
              onChange={(e) => setAirGapped(e.target.checked)}
            />
            Air-gapped (offline validation)
          </label>
        </div>
      </div>
      {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => void submit()}
          className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {submitting ? 'Minting…' : 'Mint'}
        </button>
      </div>
    </ModalShell>
  );
}

function RevokeModal({
  row,
  onCancel,
  onDone,
}: {
  row: LicenseRow;
  onCancel: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    if (!reason.trim()) {
      setError('A reason is required (it lands in the audit log).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/licenses/${row.id}/revoke`, {
        method: 'POST',
        body: { reason: reason.trim() },
      });
      await onDone();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.status === 409
            ? 'Already revoked.'
            : `HTTP ${err.status}`
          : 'Failed.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onCancel} title="Revoke license">
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Revokes <span className="font-medium">{row.display_name}</span>. Every
        linked instance is blocked on its next license-authority call. This
        cannot be undone.
      </p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        placeholder="Reason (audit trail), e.g. refund / contract ended / abuse"
        className="mt-3 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
      />
      {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={() => void submit()}
          className="rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
        >
          {submitting ? 'Revoking…' : 'Revoke'}
        </button>
      </div>
    </ModalShell>
  );
}

function RotateKeysModal({ onClose }: { onClose: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKid, setNewKid] = useState<string | null>(null);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api<{ new_kid: string }>('/v1/admin/licenses/rotate-keys', {
        method: 'POST',
      });
      setNewKid(res.new_kid);
    } catch (err) {
      setError(err instanceof ApiError ? `HTTP ${err.status}` : 'Failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalShell onClose={onClose} title="Rotate signing keys">
      {newKid ? (
        <>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            New active signing key:{' '}
            <code className="font-mono text-xs">{newKid}</code>. Previous keys
            stay published in the JWKS so already-issued{' '}
            <code className="font-mono">.orbotolic</code> files keep verifying.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
            >
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Generates a new Ed25519 keypair and marks it active. New licenses
            sign under it; existing files keep verifying against the rotated keys
            still in the JWKS. Audited as{' '}
            <code className="font-mono text-xs">license.rotate_keys</code>.
          </p>
          {error && <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => void submit()}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {submitting ? 'Rotating…' : 'Rotate'}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ---- shared bits --------------------------------------------------

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <h2 className="mb-3 text-base font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-gray-500">{label}</div>
      {children}
    </div>
  );
}

function CopyField({
  label,
  value,
  mono,
  multiline,
}: {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
}) {
  const [copied, setCopied] = useTransientState(false, 1500);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // clipboard blocked — operator can still select the text manually
    }
  }
  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
        <button
          type="button"
          onClick={() => void copy()}
          className="text-xs text-brand-700 hover:underline"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div
        className={`max-h-32 overflow-auto break-all rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-1.5 text-xs ${
          mono ? 'font-mono' : ''
        } ${multiline ? 'whitespace-pre-wrap' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

